'use client';

import { type FC, type ReactNode, useState } from 'react';

import { ChevronDown } from '@gds/Icons/Paths/ChevronDown';
import { Typography } from '@gds/Typography/Typography';

import type { FooterGroup } from '../Footer/Footer.entity';
import {
  itemOnClick,
  TrackedFooterLink,
  trackingFunction,
} from '../TrackedFooterLink/TrackedFooterLink';

import styles from './FooterLinks.module.css';

export const FooterLinks: FC<{
  columns: FooterGroup[] | undefined;
  lastColumnSlot?: ReactNode;
}> = ({ columns, lastColumnSlot }) => {
  const [expandedGroup, setExpandedGroup] = useState<Record<string, boolean>>({
    'group0-0': true,
  });

  const toggleMenu = (group: string, heading: string) => {
    itemOnClick({
      fn: trackingFunction,
      obj: { label: heading },
    });
    setExpandedGroup(expandedGroup => ({
      ...expandedGroup,
      [group]: !expandedGroup[group],
    }));
  };

  return (
    <>
      {columns?.map(({ footerLinksGroup }, columnIndex, { length }) => (
        <div
          className={styles.groups}
          key={footerLinksGroup?.map(({ heading }) => heading).join()}
          data-has-slot={(columnIndex === length - 1 && !!lastColumnSlot) || undefined}
        >
          {footerLinksGroup?.map(({ heading, footerLinkItems }, groupIndex) => (
            <div className={styles.group} key={heading}>
              <button
                data-expanded={expandedGroup[`group${columnIndex}-${groupIndex}`] ?? false}
                onClick={() => toggleMenu(`group${columnIndex}-${groupIndex}`, heading)}
              >
                <Typography Component="span" variant="body2">
                  {heading}
                </Typography>
                {/* Decorative icon; hide from accessibility tree to avoid affecting button name */}
                <ChevronDown fontSize={16} aria-hidden />
              </button>
              <ul
                data-expanded={expandedGroup[`group${columnIndex}-${groupIndex}`] ?? false}
                className={styles.menuList}
                data-test-id="footer-group-links"
              >
                {footerLinkItems?.map(({ label, href, footerLinkTarget }) => (
                  <li className={styles.menuListItem} key={label}>
                    {href ? (
                      <TrackedFooterLink
                        prefetch={false}
                        href={href}
                        trackingObj={{ category: 'footer_navigation', label, href }}
                        {...(footerLinkTarget?.length && { target: '_blank' })}
                      >
                        <Typography Component="span" variant="body4">
                          {label}
                        </Typography>
                      </TrackedFooterLink>
                    ) : (
                      <Typography Component="span" variant="body4">
                        {label}
                      </Typography>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
          {columnIndex === length - 1 && lastColumnSlot}
        </div>
      ))}
      {!columns?.length && lastColumnSlot}
    </>
  );
};
